import { msg } from "shared/Messages";
import { currentOrganisationPath, currentProjectPath } from "src/lib/PortalPaths";
import { PROJECT_SETTINGS_CATALOG } from "./ProjectSettingsCatalog";

/** Shared destinations for links to portal pages and controls. */
export const ELEMENT_REFERENCES = {
  projectSettings: {
    path: currentProjectPath("/settings"),
    id: "project-settings",
    label: msg("Project settings"),
  },
  projectName: {
    path: currentProjectPath("/settings"),
    id: "project-name",
    label: msg("Project name"),
  },
  projectDescription: {
    path: currentProjectPath("/settings"),
    id: "project-description",
    label: msg("Project description"),
  },
  projectId: { path: currentProjectPath("/settings"), id: "project-id", label: msg("Project ID") },
  apiKeys: { path: currentProjectPath("/api-keys"), id: "", label: msg("API keys") },
  newApiKey: {
    path: currentProjectPath("/api-keys"),
    id: "new-api-key",
    label: msg("New key"),
    permission: "api-keys:write",
  },
  tryPolicy: { path: currentProjectPath(), id: "try-policy", label: msg("Try your policy") },
  uploadPresets: {
    path: currentProjectPath("/upload-presets"),
    id: "",
    label: msg("Upload presets"),
  },
  hashLists: { path: currentProjectPath("/hash-lists"), id: "", label: msg("Hash lists") },
  lenses: { path: currentProjectPath("/lenses"), id: "", label: msg("Lenses") },
  connectors: {
    path: currentProjectPath("/connectors"),
    id: "",
    label: msg("Webhooks & connectors"),
  },
  metadata: { path: currentProjectPath("/metadata"), id: "", label: msg("Metadata fields") },
  inviteMember: {
    path: currentProjectPath("/members"),
    id: "invite-member",
    label: msg("Invite member"),
    permission: "members:write",
  },
  members: { path: currentProjectPath("/members"), id: "", label: msg("Project members") },
  moderationPolicy: {
    path: currentOrganisationPath("/moderation-policies"),
    id: "",
    label: msg("Moderation policies"),
  },
  profile: { path: "/portal/profile", id: "", label: msg("Profile") },
  billing: { path: currentOrganisationPath("/billing"), id: "", label: msg("Usage and billing") },
  organisation: {
    path: currentOrganisationPath("/members"),
    id: "",
    label: msg("Organisation members"),
  },
  usage: { path: currentProjectPath("/usage"), id: "", label: msg("Project usage") },
  alerts: { path: currentProjectPath("/alerts"), id: "", label: msg("Alerts & Events") },
  appeals: { path: currentProjectPath("/appeals"), id: "", label: msg("Appeals") },

  policyAddAllowance: {
    path: currentOrganisationPath("/moderation-policies/current/violence"),
    id: "add-allowance",
    label: msg("Add allowance"),
  },
  policyAddRestriction: {
    path: currentOrganisationPath("/moderation-policies/current/violence"),
    id: "add-restriction",
    label: msg("Add restriction"),
  },
  policyCategory: {
    path: currentOrganisationPath("/moderation-policies/current/violence"),
    id: "category-settings",
    label: msg("Violence settings"),
  },
  policyExamples: {
    path: currentOrganisationPath("/moderation-policies/current/violence"),
    id: "policy-examples",
    label: msg("Examples"),
  },
  policyTestDraft: {
    path: currentOrganisationPath("/moderation-policies/current"),
    id: "test-the-draft",
    label: msg("Test the draft"),
  },
  policyDuplicate: {
    path: currentOrganisationPath("/moderation-policies/current"),
    id: "duplicate-policy",
    label: msg("Duplicate"),
  },
  policyNew: {
    path: currentOrganisationPath("/moderation-policies"),
    id: "new-policy",
    label: msg("New policy"),
  },
  policyChange: {
    path: currentOrganisationPath("/moderation-policies"),
    id: "change-policy",
    label: msg("Change…"),
  },
  newConnector: {
    path: currentProjectPath("/connectors"),
    id: "new-connector",
    label: msg("New connector"),
  },
  newUploadPreset: {
    path: currentProjectPath("/upload-presets"),
    id: "new-upload-preset",
    label: msg("New preset"),
  },
  assets: { path: currentProjectPath(), id: "", label: msg("Assets") },

  ...PROJECT_SETTINGS_CATALOG,
} as const;

export type ElementReference = keyof typeof ELEMENT_REFERENCES;

export const elementReferenceHref = (setting: ElementReference) => {
  const { path, id } = ELEMENT_REFERENCES[setting];
  return id ? `${path}#${id}` : path;
};
